/**
 * @fileoverview This file contains all the core type definitions and enums
 * used throughout the AmplifyChat application.
 */

// Enum for the social media platforms supported.
export enum Platform {
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  X = 'X',
  YouTube = 'YouTube',
  Mastodon = 'Mastodon',
  Threads = 'Threads',
  Bluesky = 'Bluesky',
  TikTok = 'TikTok',
}

// Type defining the possible statuses of a post.
export type PostStatus = 'scheduled' | 'posted' | 'failed' | 'archived';
export type FacebookPublishStatus = 'pending' | 'published' | 'failed';
export type InstagramPublishStatus = 'pending' | 'published' | 'failed';

export interface FacebookPublishResult {
  pageId: string;
  success: boolean;
  facebookPostId?: string;
  errorMessage?: string;
  publishedAt?: string; // ISO string
}

export interface InstagramPublishResult {
  accountId: string;
  success: boolean;
  instagramMediaId?: string;
  errorMessage?: string;
  publishedAt?: string;
}

export type BillingCycle = 'monthly' | 'yearly';

// Type defining the supported media types.
export type MediaType = 'image' | 'video';

// Type defining the visibility options for a post on supported platforms.
export type PostVisibility = 'public' | 'friends' | 'private';

// Interface representing a social media post object.
export interface Post {
  id: string;
  platforms: Platform[]; // Platforms this post is targeted for.
  content: string; // The text content of the post.
  mediaUrl: string | null; // A base64 or external URL for the attached media.
  mediaType: MediaType | null; // The type of media attached.
  scheduledAt: Date; // The date and time the post is scheduled for.
  status: PostStatus; // The current status of the post.
  visibility?: Partial<Record<Platform, PostVisibility>>; // Platform-specific visibility settings.
  facebookPageIds?: string[]; // Target Facebook Page IDs for publishing.
  facebookStatus?: FacebookPublishStatus;
  facebookPublishResults?: FacebookPublishResult[];
  facebookLastPublishedAt?: Date;
  instagramAccountIds?: string[];
  instagramStatus?: InstagramPublishStatus;
  instagramPublishResults?: InstagramPublishResult[];
  instagramLastPublishedAt?: Date;
  analytics?: {
    reach: number;
    engagement: number;

    clicks: number;
  };
}

// Interface representing a media item in the library.
export interface Media {
  id: string;
  url: string; // base64 string or external URL for the media.
  type: MediaType; // The type of media.
  createdAt: Date; // When the media was uploaded.
  description: string; // A description or alt-text for the media.
  width?: number; // Width in pixels.
  height?: number; // Height in pixels.
  size?: number; // Size in bytes.
  storagePath?: string; // Location in cloud storage (if applicable).
}

// Interface representing a connected social media account profile.
export interface Account {
  id: string;
  platform: Platform;
  username: string;
}

// Interface representing a saved draft of a post from the composer.
export interface ComposerDraft {
  content: string;
  platforms: Platform[];
  mediaUrl: string | null;
  mediaType: MediaType | null;
  mediaDescription: string | null;
  visibility: Partial<Record<Platform, PostVisibility>>;
  scheduleLater: boolean;
  scheduledAt: string; // Stored as ISO string slice
  facebookPageIds: string[];
  facebookStatus?: FacebookPublishStatus;
  facebookPublishResults?: FacebookPublishResult[];
  instagramAccountIds?: string[];
  instagramStatus?: InstagramPublishStatus;
  instagramPublishResults?: InstagramPublishResult[];
}

export interface FacebookPageConnection {
  id: string;
  pageName: string;
  tasks: string[];
  connectedAt: Date | null;
  updatedAt: Date | null;
  userTokenExpiresAt: Date | null;
  requiresReauthorization: boolean;
}

export interface InstagramAccountConnection {
  id: string;
  username: string;
  linkedPageId: string | null;
  connectedAt: Date | null;
  updatedAt: Date | null;
  tokenExpiresAt: Date | null;
  requiresReauthorization: boolean;
}

// Interface representing a user profile.
export interface User {
  id:string;
  name: string;
  email: string;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  dob?: string | null;
  profile_complete?: boolean;
  password?: string;
  workspaces: string[]; // IDs of workspaces the user is a member of.
}

// --- Subscription and Workspace Types ---

// --- Subscription and Workspace Types ---

export type Tier = 'free' | 'standard' | 'professional' | 'premium';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete';

export type SubscriptionPlan = Tier;

export interface Subscription {
  plan: SubscriptionPlan;
  billingCycle: BillingCycle;
  usage: {
    posts: {
      count: number;
      resetDate: string;
    };
  };
}

export type WorkspaceRole = 'owner' | 'admin' | 'publisher' | 'viewer';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export type JoinRequestStatus = 'pending' | 'approved' | 'declined' | 'canceled';

// Interface representing a workspace (tenant).
export interface Workspace {
  id: string;
  name: string;
  owner_id: string; // Backend snake_case
  ownerId?: string; // Frontend camelCase convenience
  // Backend uses snake_case by default with SQLModel unless we use aliases.
  // Let's check the backend response trace: "owner_id":"user_test_123"
  tier: Tier;
  subscription_status: SubscriptionStatus;
  billing_period: BillingCycle;
  stripe_customer_id?: string | null;
  created_at?: string;
  
  accounts?: SocialAccount[]; // Matches backend relationship
  members?: string[]; // Array of User IDs
  adminIds?: string[]; // Frontend convenience

  // Frontend convenience properties (populated by hooks/API)
  subscription?: Subscription | null;
  memberRoles?: Record<string, WorkspaceRole>;
}

export interface SocialAccount {
    id: string;
    platform: Platform;
    username: string;
    workspace_id: string;
}

// Interface for the data associated with a single workspace.
export interface WorkspaceData {
  posts: Post[];
  mediaLibrary: Media[];
  accounts: Record<Platform, Account[]>;
}

export interface ApprovalRequest {
  id: string;
  postId: string;
  requesterId: string;
  approverId?: string;
  status: ApprovalStatus;
  comment?: string;
  createdAt: Date;
  resolvedAt?: Date;
}

export interface JoinRequest {
  id: string;
  userId: string;
  email: string;
  name?: string;
  message?: string | null;
  requestedRole: WorkspaceRole;
  status: JoinRequestStatus;
  createdAt: Date;
  resolvedAt?: Date;
  resolvedBy?: string | null;
  resolutionComment?: string | null;
  grantedRole?: WorkspaceRole;
}
